import { useCallback } from 'react'

import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { ApproveTransactionDialogContent } from '~/components/smart-accounts/ApproveTransactionDialogContent'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrgId } from '~/hooks/org'
import { CreateTransactionData } from '~/hooks/smart-account-actions/createTransactionData.type'
import { useServerRequests } from '~/hooks/useRequests'
import { useTransaction } from '~/hooks/useTransactions'
import {
  BackendLoggingData,
  BasicTransactionType,
  OrgTransaction,
} from '~/types'
import { InfiniteTransactionsResponse } from '~/types/query-types'

type CreateTransactionParams = {
  transactionId: string | undefined
  txData: CreateTransactionData
  dontOpenDialog: boolean
  loggingData?: BackendLoggingData
}

// After creating a tx, we need to load the full transaction so we get
// the tenderly simulation (and any other slow/heavy data that was not
// returned from the create endpoint)
const ApproveTransactionDialogContentFullTransaction = ({
  transaction,
  onConfirmation,
  closeOnSubmit,
}: {
  transaction: OrgTransaction
  onConfirmation?: () => void
  closeOnSubmit?: boolean
}) => {
  const { setGlobalModalState } = useAuth()
  const { transaction: fullTransaction } = useTransaction(transaction.id)

  return (
    <ApproveTransactionDialogContent
      transaction={(fullTransaction as OrgTransaction) ?? transaction}
      globalModal
      onClose={() => {
        setGlobalModalState({ isOpen: false, content: null })
      }}
      onConfirmation={onConfirmation}
      closeOnSubmit={closeOnSubmit}
    />
  )
}

const _verifyTransactionData = (data: CreateTransactionData) => {
  if (data.types.includes(BasicTransactionType.Transfer)) {
    if (data.transactionData?.transferData === undefined)
      throw new Error('Transfer data is required')
  }
  if (data.types.includes(BasicTransactionType.Swap)) {
    if (data.transactionData?.swapData === undefined)
      throw new Error('Swap data is required')
  }
  if (data.types.includes(BasicTransactionType.Bridge)) {
    if (data.transactionData?.bridgeData === undefined)
      throw new Error('Bridge data is required')
  }
  if (data.types.includes(BasicTransactionType.Earn)) {
    if (data.transactionData?.earnData === undefined)
      throw new Error('Earn data is required')
  }
}

export const useCreateTransaction = (): {
  createTransaction: (
    args: CreateTransactionParams,
  ) => Promise<OrgTransaction | undefined>
  isProcessing: boolean
} => {
  const { isProcessing, serverPostRequest, serverPutRequest } =
    useServerRequests()
  const queryClient = useQueryClient()
  const { setGlobalModalState } = useAuth()
  const orgId = useActiveOrgId()

  const createTransaction = useCallback(
    async ({
      transactionId,
      txData,
      dontOpenDialog,
      loggingData,
    }: CreateTransactionParams) => {
      _verifyTransactionData(txData)
      const transactionData = {
        name: txData.name,
        smartAccountId: txData.smartAccountId,
        chainId: txData.chainId,
        transactionData: txData.transactionData,
        isOfframp: txData.isOfframp,
        calls: txData.calls.map((callData) => {
          return {
            ...callData,
            value: callData.value.toString(),
          }
        }),
        memo: txData.memo,
        types: txData.types,
        walletConnectAppName: txData.walletConnectAppName,
        walletConnectAppIcon: txData.walletConnectAppIcon,
        walletConnectAppDomain: txData.walletConnectAppDomain,
        walletConnectSessionRequestId: txData.walletConnectSessionRequestId,
        walletConnectSessionRequestTopic:
          txData.walletConnectSessionRequestTopic,
      }

      let data:
        | {
            transaction: OrgTransaction
          }
        | undefined = undefined
      if (transactionId) {
        const result = await serverPutRequest<{
          transaction: OrgTransaction
        }>({
          path: `/api/smart_accounts/transactions/${transactionId}/prepare`,
          params: {
            ...transactionData,
            ...(loggingData
              ? {
                  loggingData: JSON.stringify(loggingData),
                }
              : {}),
          },
          errorMessage: 'Failed to prepare transaction',
        })
        data = result?.data

        if (data && orgId) {
          // Update the transactions list in cache
          queryClient.setQueriesData(
            {
              queryKey: [QUERY_KEYS.TRANSACTIONS],
              predicate: (query) => {
                return (
                  Array.isArray(query.queryKey) &&
                  (query.queryKey[1]?.smartAccountId ===
                    data!.transaction.smartAccountId ||
                    query.queryKey[1]?.orgId === orgId)
                )
              },
            },
            (
              oldData:
                InfiniteData<InfiniteTransactionsResponse, unknown> | undefined,
            ) => {
              if (!oldData) return oldData

              const updatedTransactions = oldData.pages.map((page) => {
                return {
                  transactions: page.transactions.map((t) => {
                    if (!t.isSplitsTransaction) return t
                    if (t.id !== data!.transaction.id) return t

                    return {
                      ...t,
                      status: data!.transaction.status,
                    }
                  }),
                  nextPage: page.nextPage,
                }
              })

              return {
                ...oldData,
                pages: updatedTransactions.map(
                  ({ transactions, nextPage }) => ({
                    transactions,
                    nextPage,
                  }),
                ),
              }
            },
          )

          // Also update the individual transaction in cache if it exists
          queryClient.setQueryData(
            [QUERY_KEYS.TRANSACTION, orgId, data.transaction.id],
            (oldData: OrgTransaction | undefined) => {
              if (!oldData) return

              return {
                ...oldData,
                status: data!.transaction.status,
              }
            },
          )
        }
      } else {
        const result = await serverPostRequest<{
          transaction: OrgTransaction
        }>({
          path: '/api/smart_accounts/transactions',
          params: {
            ...transactionData,
            ...(loggingData
              ? {
                  loggingData: JSON.stringify(loggingData),
                }
              : {}),
          },
          errorMessage: 'Failed to create transaction',
        })
        data = result?.data
        if (data) {
          const transaction = data.transaction
          queryClient.setQueryData(
            [QUERY_KEYS.TRANSACTION, transaction.orgId, transaction.id],
            transaction,
          )
        }
      }

      if (data) {
        const transaction = data.transaction
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.TRANSACTIONS],
          predicate: (query) => {
            return (
              Array.isArray(query.queryKey) &&
              query.queryKey[1]?.smartAccountId === transaction.smartAccountId
            )
          },
        })

        toast.success('Transaction created', {
          id: transaction.id,
          description: <TransactionTitle transaction={transaction} unstyled />,
        })

        txData.onSuccess?.(transaction)

        if (!dontOpenDialog) {
          const openModal = () => {
            setGlobalModalState({
              isOpen: true,
              content: (
                <ApproveTransactionDialogContentFullTransaction
                  transaction={transaction}
                  onConfirmation={txData.onConfirmation}
                  closeOnSubmit={txData.closeOnSubmit}
                />
              ),
            })
          }

          if (txData.skipModalDelay) {
            openModal()
          } else {
            // Add a slight delay so if user is redirected to a different page, the modal does not prevent navigation
            setTimeout(openModal, 500)
          }
        }

        return transaction
      }
    },
    [
      orgId,
      queryClient,
      serverPostRequest,
      serverPutRequest,
      setGlobalModalState,
    ],
  )

  return {
    createTransaction,
    isProcessing,
  }
}
